import { FIRMA } from "@/lib/branding";
import { INVOICE_TYPES } from "@/lib/constants";

export const fmtCurrency = (n, currency = "EUR") =>
  new Intl.NumberFormat(currency === "CHF" ? "de-CH" : "de-DE", {
    style: "currency",
    currency: currency || "EUR",
  }).format(Number(n || 0));

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

export const positionQuantity = (position = {}) => {
  // `quantity` is deliberately new. Older documents may contain `menge`
  // while their `preis` already stores the full line amount.
  const raw = position.quantity ?? 1;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : 1;
};

// Zeigt die rechnerische Zeilensumme einer Position – auch bei optionalen
// Angebotsvarianten, die bewusst noch nicht im Dokumentgesamtbetrag liegen.
export const positionLineNet = (position = {}) => {
  let value = Number(position.preis || 0) * positionQuantity(position);
  const discount = Math.max(0, Number(position.discount_value || 0));
  if (position.discount_type === "percent") value -= value * Math.min(discount, 100) / 100;
  if (position.discount_type === "amount") value -= Math.min(discount, value);
  return roundMoney(Math.max(0, value));
};

// Nur aktiv ausgewählte Positionen fließen in den Dokumentgesamtbetrag ein.
export const positionNet = (position = {}) =>
  position.include_in_total ? positionLineNet(position) : 0;

// Manuelle Preiszeilen gehören ausschließlich zum aktuellen Dokument. Sie
// werden nicht in den Leistungskatalog übernommen und bleiben deshalb strikt
// von gespeicherten `positions` getrennt.
export const manualBillingLines = (quote = {}) =>
  Array.isArray(quote.manual_billing_lines)
    ? quote.manual_billing_lines.filter((line) => line && typeof line === "object")
    : [];

export const manualBillingLineNet = (line = {}) =>
  roundMoney(Math.max(0, Number(line.quantity || 0) * Number(line.unit_price || 0)));

export const manualBillingPositions = (quote = {}) =>
  manualBillingLines(quote).map((line, index) => ({
    id: line.id || `manual-${index + 1}`,
    titel: line.title || (index === 0 ? quote.leistung_titel || "Leistung" : `Weitere Leistung ${index + 1}`),
    bullets: line.description
      ? [line.description]
      : index === 0 && quote.leistung_beschreibung
        ? [quote.leistung_beschreibung]
        : [],
    preis: Number(line.unit_price || 0),
    quantity: Number(line.quantity || 1),
    einheit: "",
    include_in_total: true,
    tax_rate: quote.tax_rate,
    manual_billing: true,
  }));

// Eine manuelle Zeile und eine gespeicherte Position können bewusst zusammen
// in einem Dokument vorkommen. Ältere Dokumente behalten unverändert ihren
// bisherigen Ein-Zeilen-Modus.
export const documentPositions = (quote = {}) => {
  const savedPositions = Array.isArray(quote.positions) ? quote.positions : [];
  const manualPositions = manualBillingPositions(quote);
  if (manualPositions.length) return [...manualPositions, ...savedPositions];
  if (savedPositions.length) return savedPositions;
  return [{
    id: "legacy-manual-line",
    titel: quote.leistung_titel || "Leistung",
    bullets: quote.leistung_beschreibung ? [quote.leistung_beschreibung] : [],
    preis: Number(quote.preis_pro_bewertung || 0),
    quantity: Number(quote.anzahl_bewertungen || 1),
    einheit: "",
    include_in_total: true,
    tax_rate: quote.tax_rate,
    legacy_manual_billing: true,
  }];
};

export const computeAmounts = (q) => {
  const positions = documentPositions(q);
  const groups = new Map();
  positions.forEach((position) => { const rate = Number(position.tax_rate ?? q.tax_rate ?? 0); groups.set(rate, roundMoney((groups.get(rate) || 0) + positionNet(position))); });
  const beforeDiscount = roundMoney([...groups.values()].reduce((sum, value) => sum + value, 0));
  let documentDiscount = 0;
  if (q.document_discount_type === "percent") documentDiscount = roundMoney(beforeDiscount * Math.min(Math.max(0, Number(q.document_discount_value || 0)), 100) / 100);
  if (q.document_discount_type === "amount") documentDiscount = roundMoney(Math.min(beforeDiscount, Math.max(0, Number(q.document_discount_value || 0))));
  if (documentDiscount && beforeDiscount) groups.forEach((value, rate) => groups.set(rate, roundMoney(Math.max(0, value - documentDiscount * value / beforeDiscount))));
  const zeroTax = q.tax_profile === "kleinunternehmer" || q.tax_profile === "reverse_charge" || q.reverse_charge;
  const tax_groups = [...groups.entries()].sort(([a], [b]) => a - b).map(([rate, net]) => { const effective_rate = zeroTax ? 0 : rate; const tax = roundMoney(net * effective_rate / 100); return { rate, effective_rate, net, tax, gross: roundMoney(net + tax) }; });
  const net = roundMoney(tax_groups.reduce((sum, group) => sum + group.net, 0));
  const tax = roundMoney(tax_groups.reduce((sum, group) => sum + group.tax, 0));
  const gross = roundMoney(net + tax);
  const dval = Number(q.deposit_value || 0);
  const deposit = dval > 0 ? roundMoney(q.deposit_type === "amount" ? Math.min(dval, gross) : gross * dval / 100) : 0;
  const prepaid = q.dokument_typ === "schlussrechnung" ? Math.min(roundMoney(q.prepaid_amount || 0), gross) : 0;
  const skonto_amount = roundMoney(gross * Math.min(Math.max(0, Number(q.skonto_percent || 0)), 100) / 100);
  return { net, tax, gross, tax_groups, document_discount: documentDiscount, deposit, rest: roundMoney(gross - deposit), prepaid, due: roundMoney(gross - prepaid), skonto_amount, skonto_gross: roundMoney(gross - skonto_amount), currency: q.currency || "EUR" };
};

export const quoteNet = (q) => computeAmounts(q).net;

export const quoteTotal = (q) => computeAmounts(q).gross;

// ---- Swiss QR-Rechnung (Swiss Payments Code, Version 0200) ----
export const normalizeIban = (iban) => (iban || "").replace(/\s+/g, "").toUpperCase();

// Eine sichtbare IBAN allein genügt nicht: Zahlungs-QR-Codes dürfen nur mit
// einer formal gültigen IBAN erzeugt werden. Die Modulo-97-Prüfung arbeitet
// stellenweise, damit keine ungenauen JavaScript-Zahlen entstehen.
export const isValidIban = (value) => {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`;
  let remainder = 0;
  for (const character of rearranged) {
    const digits = /[A-Z]/.test(character) ? String(character.charCodeAt(0) - 55) : character;
    for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
};

const countryCode = (value, fallback = "CH") => {
  const normalized = String(value || "").trim().toUpperCase();
  const countries = {
    DE: "DE", DEUTSCHLAND: "DE", GERMANY: "DE",
    AT: "AT", ÖSTERREICH: "AT", OESTERREICH: "AT", AUSTRIA: "AT",
    CH: "CH", SCHWEIZ: "CH", SWITZERLAND: "CH",
    LI: "LI", LIECHTENSTEIN: "LI",
    FR: "FR", FRANKREICH: "FR", FRANCE: "FR",
    IT: "IT", ITALIEN: "IT", ITALY: "IT",
    NL: "NL", NIEDERLANDE: "NL", NETHERLANDS: "NL",
    BE: "BE", BELGIEN: "BE", BELGIUM: "BE",
    LU: "LU", LUXEMBURG: "LU", LUXEMBOURG: "LU",
    ES: "ES", SPANIEN: "ES", SPAIN: "ES",
    PL: "PL", POLEN: "PL", POLAND: "PL",
    CZ: "CZ", TSCHECHIEN: "CZ", CZECHIA: "CZ",
    GB: "GB", GROßBRITANNIEN: "GB", GROSSBRITANNIEN: "GB", "UNITED KINGDOM": "GB",
    US: "US", USA: "US", "VEREINIGTE STAATEN": "US",
  };
  return countries[normalized] || fallback;
};

const splitStreetAndNumber = (value) => {
  const address = String(value || "").trim();
  const match = address.match(/^(.*?)[,\s]+(\d[\dA-Za-z/-]*)$/);
  return match ? [match[1].trim(), match[2].trim()] : [address, ""];
};

const splitPostalAndTown = (value) => {
  const address = String(value || "").trim();
  const match = address.match(/^(\d{4,6})\s+(.+)$/);
  return match ? [match[1].trim(), match[2].trim()] : ["", address];
};

// Bankverbindung pro Dokument (optionaler Override) sonst Org-Steuerdaten.
export const effectiveBank = (steuer, quote) => ({
  iban: quote?.bank_iban || steuer?.iban || "",
  bic: quote?.bank_bic || steuer?.bic || "",
  bank: quote?.bank_name || steuer?.bank || "",
  kontoinhaber: quote?.bank_owner || steuer?.kontoinhaber || "",
});

export const isSwissQrEligible = (steuer, quote) => {
  const iban = normalizeIban(effectiveBank(steuer, quote).iban);
  const cur = (quote?.currency || "EUR").toUpperCase();
  return (
    quote?.dokument_typ === "rechnung" &&
    isValidIban(iban) &&
    iban.length === 21 &&
    (iban.startsWith("CH") || iban.startsWith("LI")) &&
    (cur === "CHF" || cur === "EUR")
  );
};

// Erstellt den SPC-Datenstring für den Schweizer QR-Code.
export const buildSwissQrPayload = (steuer, quote) => {
  const a = computeAmounts(quote);
  const eb = effectiveBank(steuer, quote);
  const iban = normalizeIban(eb.iban);
  const creditorName = (eb.kontoinhaber || FIRMA.firma || FIRMA.name || "").slice(0, 70);
  const [creditorStreet, creditorBuildingNumber] = splitStreetAndNumber(FIRMA.strasse);
  const [creditorPostalCode, creditorTown] = splitPostalAndTown(FIRMA.plz_ort);
  const creditorCountry = countryCode(FIRMA.land, iban.startsWith("LI") ? "LI" : "CH");
  const debtorName = (quote.kunde_firma || quote.kunde_name || "").slice(0, 70);
  const [debtorStreet, debtorBuildingNumber] = splitStreetAndNumber(quote.kunde_strasse);
  const [debtorPostalCode, debtorTown] = splitPostalAndTown(quote.kunde_plz_ort);
  const debtorCountry = countryCode(quote.kunde_land, "CH");
  const amount = a.gross > 0 ? a.gross.toFixed(2) : "";
  const currency = (quote.currency || "CHF").toUpperCase();
  const message = (quote.angebot_nr ? `Rechnung ${quote.angebot_nr}` : "").slice(0, 140);
  const hasDebtor = !!debtorName;
  const lines = [
    "SPC", "0200", "1",
    iban,
    "S", creditorName, creditorStreet.slice(0, 70), creditorBuildingNumber.slice(0, 16), creditorPostalCode.slice(0, 16), creditorTown.slice(0, 35), creditorCountry,
    "", "", "", "", "", "", "", // Ultimate creditor (ungenutzt)
    amount, currency,
    hasDebtor ? "S" : "", debtorName, debtorStreet.slice(0, 70), debtorBuildingNumber.slice(0, 16), debtorPostalCode.slice(0, 16), debtorTown.slice(0, 35), hasDebtor ? debtorCountry : "",
    "NON", "",
    message,
    "EPD",
  ];
  return lines.join("\n");
};

// ---- Deutscher GiroCode (EPC-QR-Code, SEPA-Überweisung) ----
export const isGiroCodeEligible = (steuer, quote) => {
  const iban = normalizeIban(effectiveBank(steuer, quote).iban);
  const cur = (quote?.currency || "EUR").toUpperCase();
  return (
    INVOICE_TYPES.includes(quote?.dokument_typ) &&
    cur === "EUR" &&
    isValidIban(iban) &&
    !iban.startsWith("CH") &&
    !iban.startsWith("LI")
  );
};

// Erstellt den EPC-Datenstring (Version 002) für den GiroCode.
export const buildGiroCodePayload = (steuer, quote) => {
  const a = computeAmounts(quote);
  const eb = effectiveBank(steuer, quote);
  const iban = normalizeIban(eb.iban);
  const bic = (eb.bic || "").replace(/\s+/g, "").toUpperCase();
  const name = (eb.kontoinhaber || FIRMA.firma || FIRMA.name || "").slice(0, 70);
  const amount = a.gross > 0 ? `EUR${a.gross.toFixed(2)}` : "";
  const remittance = (quote.angebot_nr ? `Rechnung ${quote.angebot_nr}` : "").slice(0, 140);
  const lines = [
    "BCD",        // Service Tag
    "002",        // Version (002 = BIC optional)
    "1",          // Zeichensatz: 1 = UTF-8
    "SCT",        // SEPA Credit Transfer
    bic,          // BIC des Empfängers (optional in v002)
    name,         // Name des Empfängers
    iban,         // IBAN des Empfängers
    amount,       // Betrag, z. B. EUR1190.00
    "",           // Purpose (optional)
    "",           // Strukturierte Referenz (leer)
    remittance,   // Unstrukturierter Verwendungszweck
  ];
  return lines.join("\n");
};

export const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const trackingUrlFor = (token) => {
  if (!token) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/p/${encodeURIComponent(token)}`;
};

export const EU_COUNTRIES = [
  "Belgien", "Bulgarien", "Dänemark", "Estland", "Finnland", "Frankreich",
  "Griechenland", "Irland", "Italien", "Kroatien", "Lettland", "Litauen",
  "Luxemburg", "Malta", "Niederlande", "Österreich", "Polen", "Portugal",
  "Rumänien", "Schweden", "Slowakei", "Slowenien", "Spanien",
  "Tschechien", "Ungarn", "Zypern",
];

export const taxHintForCountry = (land, company = {}, documentCurrency = "") => {
  const companyCountry = String(company?.main_country || company?.land || "").trim().toUpperCase();
  const isSwissCompany = ["CH", "SCHWEIZ", "SWITZERLAND"].includes(companyCountry);
  if (isSwissCompany) {
    const currency = String(documentCurrency || company?.base_currency || "CHF").toUpperCase();
    return {
      kind: "swiss",
      title: "Währungs- und Steuerhinweis",
      short: `Rechnungsbetrag in ${currency}.`,
      long: `Dieses Dokument wird in ${currency} ausgestellt. Der anwendbare Steuerhinweis richtet sich nach dem gewählten Steuerprofil und dem Leistungsort.`,
    };
  }
  const l = (land || "").trim();
  if (!l || l.toLowerCase() === "deutschland") {
    return {
      kind: "national",
      title: "Umsatzsteuerlicher Hinweis",
      short: "Gemäß § 19 UStG keine MwSt.",
      long: "Im Rahmen der Kleinunternehmerregelung gemäß § 19 UStG wird keine Umsatzsteuer ausgewiesen.",
    };
  }
  if (EU_COUNTRIES.map((c) => c.toLowerCase()).includes(l.toLowerCase())) {
    return {
      kind: "eu",
      title: "Umsatzsteuerlicher Hinweis",
      short: "EU-Ausland · ggf. § 3a Abs. 2 UStG",
      long:
        "Leistung an einen Empfänger im EU-Ausland. Bei B2B-Leistungen kann der Leistungsort im Land des Empfängers liegen (§ 3a Abs. 2 UStG, ggf. Reverse-Charge). Bitte USt-IdNr. des Kunden angeben und im Zweifel mit Steuerberater abklären.",
    };
  }
  return {
    kind: "drittland",
    title: "Umsatzsteuerlicher Hinweis",
    short: "Drittland · § 3a Abs. 2 UStG",
    long:
      "Nicht im Inland steuerbare Leistung – der Leistungsort liegt im Drittlandsgebiet. Es wird keine deutsche Umsatzsteuer ausgewiesen. Bitte im Zweifel mit Steuerberater abklären.",
  };
};
